import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// hooks
import posthog from "posthog-js";
import { useBuildingModelConstruction } from "queries/Construction/useBuildingModelConstruction";
import { useBuildingFootprintsQuery } from "queries/useBuildingFootprintsQuery";
import useBuildingLoadingState from "recoilStore/useBuildingLoadingState";

// components
import SidebarLoading from "components/molecules/SidebarLoading";

// helpers
import { useBuildingModelUid } from "hooks/useBuildingModelUid";
import { usePermissions } from "hooks/usePermissions";
import { UserPermissions } from "utils/enums";
import BuildingRoof from "./BuildingConstructionComponents/BuildindRoof";
import BuildingAdvancedAttributes from "./BuildingConstructionComponents/BuildingAdvancedAttributes";
import BuildingFloor from "./BuildingConstructionComponents/BuildingFloor";
import BuildingOverview from "./BuildingConstructionComponents/BuildingOverview";
import BuildingPolicies from "./BuildingConstructionComponents/BuildingPolicies";
import BuildingWindows from "./BuildingConstructionComponents/BuildingWindow";
import ConstructionFooter from "./BuildingConstructionComponents/ConstructionFooter";
import ConstructionHeader from "./BuildingConstructionComponents/ConstructionHeader";
import DocumentUpload from "./BuildingConstructionComponents/DocumentUpload";
import {
    buildBuildingPropertiesMap,
    useSelectedBuildingPolygon,
    useUpdateBuildingModel,
} from "./BuildingSidebarTabs/BuildingConstruction.helpers";

function BuildingConstruction() {
    const buildingModelUid = useBuildingModelUid();

    const { data: buildingConstruction, isLoading } = useBuildingModelConstruction(buildingModelUid);
    const { selectedFootprintPolygon, setSelectedFootprintPolygon } = useSelectedBuildingPolygon(buildingConstruction);
    const { setLoadingState, loadingState } = useBuildingLoadingState(buildingModelUid);
    const { hasPermission } = usePermissions();

    const buildingAddress = buildingConstruction?.location
        ? ({
              streetAddress: buildingConstruction?.location.streetAddress,
              city: buildingConstruction?.location.city,
              stateProvince: buildingConstruction?.location.stateProvince,
              postalZipCode: buildingConstruction?.location.postalZipCode,
              country: buildingConstruction?.location.country,
              latitude: buildingConstruction?.location.latitude,
              longitude: buildingConstruction?.location.longitude,
          } as Address)
        : undefined;

    const { updateBuildingModel, error: updateBuildingModelError } = useUpdateBuildingModel(
        buildingModelUid,
        buildingAddress
    );

    // properties
    const [properties, setBuildingProperties] = useState<BuildingProperties | null>(null);

    const defaultProperties = useMemo(() => {
        if (!buildingConstruction) return null;
        return buildBuildingPropertiesMap(buildingConstruction);
    }, [buildingConstruction]);

    useEffect(() => {
        setBuildingProperties(defaultProperties);
    }, [defaultProperties]);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [keyToValidationMap, setKeyToValidationMap] = useState(new Map());

    const { isLoading: footprintsLoading, data: buildingFootprints } = useBuildingFootprintsQuery(
        buildingAddress,
        buildingConstruction?.grossFloorArea,
        buildingConstruction?.floorsAboveGrade
    );

    const updateValidationMapForKey = useCallback(
        (key: string, value: boolean) =>
            setKeyToValidationMap((prev) => {
                prev.set(key, value);
                return prev;
            }),
        [keyToValidationMap]
    );

    const localBuildingConstruction = useMemo(() => {
        if (!properties || !buildingConstruction) return null;
        return {
            ...buildingConstruction,
            ...properties,
            buildingUid: buildingConstruction.buildingUid || "",
        };
    }, [properties, buildingConstruction]);

    useEffect(() => {
        if (updateBuildingModelError) {
            setBuildingProperties(defaultProperties);
        }
    }, [updateBuildingModelError]);

    const selectedFootprintUid = useMemo(() => {
        if (buildingConstruction?.buildingFootprint)
            return buildingConstruction?.buildingFootprint.properties.buildingUid;

        return buildingConstruction?.buildingUid;
    }, [buildingConstruction]);

    const handleCancel = () => {
        setIsEditing(false);
        setBuildingProperties(defaultProperties);
        setSelectedFootprintPolygon(buildingConstruction?.buildingFootprint);
        posthog.capture("Edit-construction cancel");
    };

    const getFootprintForRequest = useCallback(() => {
        if (selectedFootprintPolygon) return selectedFootprintPolygon;

        if (buildingConstruction && buildingFootprints) {
            const { buildingUid } = buildingConstruction;

            return buildingFootprints.features.find((footprint) => footprint.properties.buildingUid === buildingUid);
        }
        return undefined;
    }, [buildingConstruction, selectedFootprintPolygon, buildingFootprints]);

    const handleSave = () => {
        if (properties) {
            const { carbonPolicyIds, ...buildingProps } = properties;
            updateBuildingModel(
                {
                    buildingProperties: buildingProps,
                    carbonPolicyIds,
                    buildingUid: selectedFootprintPolygon?.properties.buildingUid || buildingProps.buildingUid,
                    buildingFootprint: getFootprintForRequest(),
                },
                {
                    onSuccess: () => {
                        navigate(`/buildings/${buildingModelUid}`);
                    },
                }
            );
            setLoadingState((prev) => ({
                ...prev,
                constructionSidebarLoading: true,
                buildingReportsLoading: true,
            }));
        }
        setIsEditing(false);
    };

    if (isLoading || footprintsLoading || loadingState.constructionSidebarLoading) return <SidebarLoading />;

    if (!localBuildingConstruction || !properties) return null;

    return (
        <div className={classNames("construction-sidebar", isEditing && "editing")}>
            <ConstructionHeader
                isEditing={isEditing}
                handleCancel={handleCancel}
                handleEdit={() => setIsEditing(true)}
            />
            <div className={classNames("construction-sidebar__body", isEditing && "editing")}>
                <BuildingOverview
                    location={localBuildingConstruction.location}
                    buildingUid={localBuildingConstruction.buildingUid}
                    buildingConstruction={localBuildingConstruction}
                    setProperties={(value) => {
                        setBuildingProperties((prev) => ({
                            ...prev,
                            ...value,
                        }));
                    }}
                    buildingFootprints={buildingFootprints}
                    isEditing={isEditing}
                    updateValidationMapForKey={updateValidationMapForKey}
                    selectedFootprintUid={selectedFootprintUid || undefined}
                    selectedFootprintPolygon={selectedFootprintPolygon}
                    setSelectedFootprintPolygon={setSelectedFootprintPolygon}
                />
                <BuildingFloor
                    buildingConstruction={localBuildingConstruction}
                    buildingUid={localBuildingConstruction.buildingUid}
                    setProperties={(value) => {
                        setBuildingProperties((prev) => ({
                            ...prev,
                            ...value,
                        }));
                    }}
                    isEditing={isEditing}
                    updateValidationMapForKey={updateValidationMapForKey}
                />
                <BuildingRoof
                    buildingConstruction={localBuildingConstruction}
                    buildingUid={localBuildingConstruction.buildingUid}
                    setProperties={(value) => {
                        setBuildingProperties((prev) => ({
                            ...prev,
                            ...value,
                        }));
                    }}
                    isEditing={isEditing}
                    updateValidationMapForKey={updateValidationMapForKey}
                />
                <BuildingWindows
                    buildingConstruction={localBuildingConstruction}
                    buildingUid={localBuildingConstruction.buildingUid}
                    setProperties={(value) => {
                        setBuildingProperties((prev) => ({
                            ...prev,
                            ...value,
                        }));
                    }}
                    isEditing={isEditing}
                    updateValidationMapForKey={updateValidationMapForKey}
                />
                <BuildingPolicies
                    buildingConstruction={localBuildingConstruction}
                    buildingUid={localBuildingConstruction.buildingUid}
                    setProperties={(value) => {
                        setBuildingProperties((prev) => ({
                            ...prev,
                            ...value,
                        }));
                    }}
                    isEditing={isEditing}
                    updateValidationMapForKey={updateValidationMapForKey}
                />
                {hasPermission(UserPermissions.UseInternalTools) && (
                    <DocumentUpload
                        buildingConstruction={localBuildingConstruction}
                        buildingUid={localBuildingConstruction.buildingUid}
                        setProperties={(value) => {
                            setBuildingProperties((prev) => ({
                                ...prev,
                                ...value,
                            }));
                        }}
                        isEditing={isEditing}
                        updateValidationMapForKey={updateValidationMapForKey}
                    />
                )}
                <BuildingAdvancedAttributes
                    buildingConstruction={localBuildingConstruction}
                    buildingUid={localBuildingConstruction.buildingUid}
                    setProperties={(value) => {
                        setBuildingProperties((prev) => ({
                            ...prev,
                            ...value,
                        }));
                    }}
                    isEditing={isEditing}
                    updateValidationMapForKey={updateValidationMapForKey}
                />
            </div>
            {isEditing && (
                <ConstructionFooter
                    handleCancel={handleCancel}
                    handleSave={handleSave}
                    saveButtonIsDisabled={
                        Array.from(keyToValidationMap.values()).some((value) => value) ||
                        (buildingConstruction?.buildingFootprint !== undefined &&
                            selectedFootprintPolygon === undefined)
                    }
                />
            )}
        </div>
    );
}

export default BuildingConstruction;
